import { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { rootSelectors, RootState } from "state/rootReducer";
import { actions as userActions } from "state/slices/userSlice";
import { SongFeedbackAndSeed } from "state/slices/userSlice";
import SongList from "components/SongList/SongList";
import TagArray from "components/TagArray";
import Modal from "components/Modal";

interface OwnProps {
  onSaveSeeds: (seeds: Song[]) => void;
  onCancel: () => void;
}

interface StateProps {
  seeds: SongFeedbackAndSeed[];
  getSeedsWithTag: (tag: Tag) => SongFeedbackAndSeed[];
  getEnabledTagsForSong: (songGid: string) => Tag[];
}

interface DispatchProps {
  onAddTag: typeof userActions.addTag;
  onRemoveTag: typeof userActions.removeTag;
}

export type SeedsModalProps = OwnProps &
  StateProps &
  ResolveThunks<DispatchProps>;

function SeedsModal(props: SeedsModalProps): JSX.Element {
  const [selectedSongsByGid, setSelectedSongsByGid] = useState<
    Record<string, boolean>
  >(() => {
    const map: Record<string, boolean> = {};

    for (const seed of props.seeds) {
      map[seed.song.gid] = seed.seed;
    }

    return map;
  });

  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);

  const selectedCount =
    Object.values(selectedSongsByGid).filter(Boolean).length;

  function getSelectedSongs(): Song[] {
    return props.seeds
      .filter((sr) => selectedSongsByGid[sr.song.gid])
      .map((sr) => sr.song);
  }

  function getSongsForTag(tag: Tag, selected: boolean) {
    const songs: Record<string, boolean> = {};

    for (const song of props.getSeedsWithTag(tag)) {
      songs[song.song.gid] = selected;
    }

    console.log("UPDATED SONGS " + JSON.stringify(songs));

    return songs;
  }

  function selectTag(tag: Tag): void {
    if (!selectedTags.includes(tag)) {
      const newTags = [...selectedTags];
      newTags.push(tag);
      setSelectedTags(newTags);
    }
    setSelectedSongsByGid({
      ...selectedSongsByGid,
      ...getSongsForTag(tag, true),
    });
  }

  function deSelectTag(tag: Tag): void {
    setSelectedTags(selectedTags.filter((t) => t !== tag));
    setSelectedSongsByGid({
      ...selectedSongsByGid,
      ...getSongsForTag(tag, false),
    });
  }

  /** Select or clear every track. The All and None buttons were two copies of this loop. */
  function setAll(selected: boolean): void {
    const value = { ...selectedSongsByGid };

    for (const key of Object.keys(selectedSongsByGid)) {
      value[key] = selected;
    }

    setSelectedSongsByGid(value);
  }

  return (
    <Modal
      title="Select seeds"
      subtitle={`${selectedCount} of ${props.seeds.length} selected. Recommendations are drawn from the tracks you choose.`}
      onCancel={props.onCancel}
      footer={
        <>
          <button
            type="button"
            className="button-generic"
            onClick={() => props.onCancel()}
          >
            Cancel
          </button>
          {/* Not a Spotify action, so it takes the brand accent, not green. */}
          <button
            type="button"
            className="button-primary"
            onClick={() => props.onSaveSeeds(getSelectedSongs())}
          >
            Save seeds
          </button>
        </>
      }
    >
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 pb-3 mb-2 border-b border-rule">
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-wide text-muted">
            Select
          </span>
          <button
            type="button"
            className="button-generic text-sm py-1"
            onClick={() => setAll(true)}
          >
            All
          </button>
          <button
            type="button"
            className="button-generic text-sm py-1"
            onClick={() => setAll(false)}
          >
            None
          </button>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-wide text-muted">
            By tag
          </span>
          <TagArray
            onDeselect={deSelectTag}
            onSelect={selectTag}
            enabledTags={selectedTags}
          />
        </div>
      </div>

      <SongList
        maxSongs={1000}
        songs={props.seeds.map((s) => s.song)}
        selectSongHandler={() => {}}
        overridePlayHandler={(song, selected) => {
          const value = {
            ...selectedSongsByGid,
          };

          value[song.gid] = selected;

          setSelectedSongsByGid(value);
        }}
        selectedSongsByGid={selectedSongsByGid}
        otherdataName="Tags"
        otherdata={props.seeds.map((s) => (
          <TagArray
            key={s.song.gid}
            enabledTags={props.getEnabledTagsForSong(s.song.gid)}
            squareSize="1rem"
            onSelect={(tag) => props.onAddTag(s.song.gid, tag)}
            onDeselect={(tag) => props.onRemoveTag(s.song.gid, tag)}
          />
        ))}
      />
    </Modal>
  );
}

function mapState(rootState: RootState): StateProps {
  return {
    seeds: rootSelectors.user.getSeedCandidates(rootState),
    getSeedsWithTag: rootSelectors.user.getSeedsWithTags(rootState),
    getEnabledTagsForSong: rootSelectors.user.getEnabledTagsForSong(rootState),
  };
}

const mapDispatch: DispatchProps = {
  onAddTag: userActions.addTag,
  onRemoveTag: userActions.removeTag,
};

export default connect(mapState, mapDispatch)(SeedsModal);
